import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../api';
import Loading from '../../components/Loading';

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [entityTypes, setEntityTypes] = useState({});
  const [sectors, setSectors] = useState({});

  const companySizeMap = {
    "1": "1-10",
    "2": "11-50",
    "3": "51-200",
    "4": "201-500",
    "5": "501-1000"
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch company details
        const companyRes = await api.get(`/md/companies/${id}`);
        const company = companyRes.data.company;
        const contacts = companyRes.data.contacts || [];

        // Fetch entity types
        const entityTypesRes = await api.get('/md/entity-type-page');
        const entityTypesMap = {};
        entityTypesRes.data.content.forEach(type => {
          entityTypesMap[type.id] = type.entityType;
        });
        setEntityTypes(entityTypesMap);

        // Fetch sectors
        const sectorsRes = await api.get('/md/sector');
        const sectorsMap = {};
        sectorsRes.data.forEach(sector => {
          sectorsMap[sector.id] = sector.name;
        });
        setSectors(sectorsMap);

        // ✅ FIX: Transform backend data to extract IDs for fields that might be objects
        // Handle entityType - extract ID if it's an object, or find by entityType name if it's a string
        let transformedEntityType = company.entityType;
        if (transformedEntityType) {
          if (typeof transformedEntityType === 'object') {
            transformedEntityType = transformedEntityType.id || transformedEntityType;
          } else if (typeof transformedEntityType === 'string' && entityTypesRes.data.content.length > 0) {
            // If it's a string (entityType name), try to find the matching ID
            const found = entityTypesRes.data.content.find(et =>
              et.entityType === transformedEntityType || String(et.id) === String(transformedEntityType)
            );
            if (found) {
              transformedEntityType = String(found.id);
            }
          }
        }
        // Ensure it's a string for consistency
        transformedEntityType = transformedEntityType != null ? String(transformedEntityType) : '';

        // Handle sector - extract ID if it's an object, or find by name if it's a string
        let transformedSector = company.sector;
        if (transformedSector) {
          if (typeof transformedSector === 'object') {
            transformedSector = transformedSector.id || transformedSector;
          } else if (typeof transformedSector === 'string' && sectorsRes.data.length > 0) {
            // If it's a string (name), try to find the matching ID
            const found = sectorsRes.data.find(s =>
              s.name === transformedSector || String(s.id) === String(transformedSector)
            );
            if (found) {
              transformedSector = String(found.id);
            }
          }
        }
        // Ensure it's a string for consistency
        transformedSector = transformedSector != null ? String(transformedSector) : '';

        // Handle companySize - ensure it matches the format (string ID)
        let transformedCompanySize = company.companySize;
        transformedCompanySize = transformedCompanySize != null ? String(transformedCompanySize) : '';

        // Handle clientFlagging - extract ID if it's an object, or use as-is if it's already an ID
        let transformedClientFlagging = company.clientFlagging;
        if (transformedClientFlagging) {
          if (typeof transformedClientFlagging === 'object') {
            transformedClientFlagging = transformedClientFlagging.id || transformedClientFlagging;
          } else {
            // If it's already an ID (number or string), keep it
            transformedClientFlagging = transformedClientFlagging;
          }
        }
        // Ensure it's a string for consistency
        transformedClientFlagging = transformedClientFlagging != null ? String(transformedClientFlagging) : '';

        const companyDetail = {
          id: company.id,
          name: company.companyName,
          entityType: transformedEntityType,
          sector: transformedSector,
          companySize: transformedCompanySize,
          website: company.website,
          email: company.companyEmail,
          phone: company.phone,
          dateAdded: company.cdate,
          clientFlagging: transformedClientFlagging,
          address: {
            address: company.address,
            country: company.country,
            state: company.state,
            city: company.city,
            postalCode: company.postalCode,
          },
          contacts: contacts.map(c => ({
            name: `${c.firstName} ${c.lastName}`,
            title: c.title,
            email: c.email,
            phone: c.phone
          }))
        };

        setCompanyData(companyDetail);
      } catch (error) {
        console.error('Failed to fetch company detail', error);
        setCompanyData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) return <Loading />;

  // Get the base path from current location (e.g., /admin, /sales, /marketing, etc.)
  const getBasePath = () => {
    const pathParts = location.pathname.split('/');
    if (pathParts.length >= 2) {
      return `/${pathParts[1]}`;
    }
    return '/admin'; // fallback to admin
  };

  const basePath = getBasePath();

  if (!companyData) {
    return (
      <>
        <div className="container-xl py-4">
          <div className="text-center">
            <div className="alert alert-danger">
              Company not found. <a href="#" onClick={() => navigate(`/masterdata-company`)}>Return to companies list</a>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title d-flex align-items-center">
                <a className="me-2 d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); navigate(`/masterdata-company`); }}>
                  <span className="d-flex align-items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-left">
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <path d="M15 6l-6 6l6 6" />
                    </svg>
                  </span>
                </a>
                {companyData.name}
              </h2>
            </div>
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="container-xl">
          <div className="row row-cards">
            <div className="col-lg-12">
              <div className="card mb-3">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <div className="me-3">
                      <span className="avatar avatar-xl text-white" style={{ backgroundColor: '#206bc4' }}>NS</span>
                    </div>
                    <div>
                      <h2 className="mb-0">{companyData.name}</h2>
                      <div className="text-secondary">{sectors[companyData.sector] || companyData.sector}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="card mb-3">
                <div className="card-header">
                  <h3 className="card-title">Basic information</h3>
                </div>
                <div className="card-body">
                  <div className="datagrid">
                    <div className="datagrid-item">
                      <div className="datagrid-title">Company Name</div>
                      <div className="datagrid-content">{companyData.name}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Entity Type</div>
                      <div className="datagrid-content">{entityTypes[companyData.entityType] || companyData.entityType}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Sector</div>
                      <div className="datagrid-content">{sectors[companyData.sector] || companyData.sector}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Company Size</div>
                      <div className="datagrid-content">{companySizeMap[companyData.companySize] || companyData.companySize}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Website</div>
                      <div className="datagrid-content">{companyData.website}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Email</div>
                      <div className="datagrid-content">{companyData.email}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Phone</div>
                      <div className="datagrid-content">{companyData.phone}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Date Added</div>
                      <div className="datagrid-content">{companyData.dateAdded}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Client Flagging</div>
                      <div className="datagrid-content">{companyData.clientFlagging || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card mb-3">
                <div className="card-header">
                  <h3 className="card-title">Address Information</h3>
                </div>
                <div className="card-body">
                  <div className="datagrid">
                    <div className="datagrid-item">
                      <div className="datagrid-title">Address</div>
                      <div className="datagrid-content">{companyData.address.address}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Country</div>
                      <div className="datagrid-content">{companyData.address.country}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">State</div>
                      <div className="datagrid-content">{companyData.address.state}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">City</div>
                      <div className="datagrid-content">{companyData.address.city}</div>
                    </div>
                    <div className="datagrid-item">
                      <div className="datagrid-title">Postal Code</div>
                      <div className="datagrid-content">{companyData.address.postalCode}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-12">
              <div className="card mb-3">
                <div className="card-header">
                  <div className="row align-items-center w-full">
                    <div className="col">
                      <h3 className="card-title">Contacts</h3>
                    </div>
                  </div>
                </div>
                <div className="table-responsive">
                  <table className="table table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Title</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th className="w-1"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {companyData.contacts.map((contact, index) => (
                        <tr key={index}>
                          <td>{contact.name}</td>
                          <td className="text-secondary">{contact.title}</td>
                          <td className="text-secondary">
                            <a href="#" className="text-reset">{contact.email}</a>
                          </td>
                          <td className="text-secondary">{contact.phone}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyDetail;
